import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"

import { urqlClient } from "../../utils/utils"

import { SocialProofCard, SocialProofCardProps } from "./SocialProofCard"
import { SocialProofWrapper } from "./SocialProofWrapper"

// ----- GraphQL Queries -----

const APPOINTMENTS_LAST_24H_COUNT_QUERY = `
  query AppointmentsLast24hCount {
    appointmentsLast24hCount
  }
`

const LATEST_APPOINTMENTS_QUERY = `
  query LatestAppointments($lastNHours: Int!, $limit: Int!) {
    latestAppointments(lastNHours: $lastNHours, limit: $limit) {
      id
      clientDisplayName
      practice {
        listingCardImageUrl
        user {
          name
          slug
          photoUrl
          profilePhotos {
            large {
              jpeg
              webp
            }
            small {
              any
              jpeg
              webp
            }
          }
        }
      }
      createdAt
    }
  }
`

// ----- Constants -----

const LATEST_APPOINTMENTS_LIMIT = 20
const LATEST_APPOINTMENTS_LAST_N_HOURS = 48
const FALLBACK_AVATAR_URL = "/images/icons/heal_me.svg"
const APPOINTMENTS_LAST_24H_COUNT_IMAGE_URL = "/images/social-proof-component-image.png"

const DISPLAY_DURATION_MS = 6000
const DELAY_DURATION_MS = 500
const SOCIAL_PROOF_IMAGE_WIDTH_2X = 144
const APPOINTMENTS_COUNT_TICKER_START_VALUE = 1
const APPOINTMENTS_LAST_HOURS_COUNT_DISPLAY_THRESHOLD = 100
const APPOINTMENTS_COUNT_TICKER_ENTER_DELAY_MS = 400
const APPOINTMENTS_COUNT_TICKER_DURATION_MS = 1000
const APPOINTMENTS_COUNT_TICKER_MIN_TICK_MS = 40

const SOCIAL_PROOF_CARD_PROPS_EMPTY_VALUE: SocialProofCardProps = {
  imgSrc: "",
  preSubHeadingText: "",
  mainHeadingText: "",
  postSubHeadingText: ""
}

// ----- Interfaces/Types -----

interface LatestAppointmentsQueryData {
  id: string
  clientDisplayName: string
  practice: {
    listingCardImageUrl?: string
    user: {
      name: string
      slug: string
      photoUrl?: string
      profilePhotos: {
        large?: {
          jpeg?: string
          webp?: string
        }
        small: {
          jpeg?: string
          webp?: string
        }
      }
    }
  }
  createdAt: string
}

type SocialProofNotificationItemType = "appointmentsLast24hCount" | "latestAppointments"

// Can be removed, used here for clarity/debugging
// If removed, replace `notificationItemsList` state with Array<SocialProofCardProps>
interface SocialProofNotificationItem {
  type: SocialProofNotificationItemType
  data: SocialProofCardProps
  displayTime: number
  appointmentsCountTarget?: number
}

// ----- Helper Functions -----

const formatAppointmentsLast24hCountHeading = (count: number) => `${count} People booked`

const prepareAppointmentsLast24hCountProps = (count: number): SocialProofCardProps => {
  return {
    imgSrc: APPOINTMENTS_LAST_24H_COUNT_IMAGE_URL,
    preSubHeadingText: "",
    mainHeadingText: formatAppointmentsLast24hCountHeading(count),
    postSubHeadingText: "a session in the past 24 hrs 🔥"
  }
}

const treatImagePath = (path: string) => {
  const normalizedPath = path.startsWith("http") ? path : `https://cdn.filestackcontent.com/${path}`
  const match = normalizedPath.match(/^(https:\/\/cdn\.filestackcontent\.com)\/([^/]+)$/)

  if (match) {
    const [, base, handle] = match
    return `${base}/resize=width:${SOCIAL_PROOF_IMAGE_WIDTH_2X},fit:clip/${handle}`
  }

  return normalizedPath
}

const formatTimeDifference = (createdAt: string) => {
  const ts = Date.parse(createdAt)
  if (Number.isNaN(ts)) return "recently"
  const totalMinutes = Math.floor((Date.now() - ts) / 60_000)
  if (totalMinutes < 1) return "just now"
  if (totalMinutes < 60) return `${totalMinutes} min ago`
  const hours = Math.floor(totalMinutes / 60)
  return `${hours} hr ago`
}

const getLatestAppointmentImageSrc = (appointment: Partial<LatestAppointmentsQueryData>) => {
  const listingCardPhoto = appointment.practice?.listingCardImageUrl

  if (listingCardPhoto) return treatImagePath(listingCardPhoto)

  const fallbackProfilePhoto =
    appointment.practice?.user?.profilePhotos?.large?.webp ||
    appointment.practice?.user?.profilePhotos?.large?.jpeg ||
    appointment.practice?.user?.photoUrl ||
    appointment.practice?.user?.profilePhotos?.small?.jpeg ||
    appointment.practice?.user?.profilePhotos?.small?.webp

  return fallbackProfilePhoto ? treatImagePath(fallbackProfilePhoto) : FALLBACK_AVATAR_URL
}

const prepareLatestAppointmentsProps = (appointment: Partial<LatestAppointmentsQueryData>): SocialProofCardProps => {
  const imgSrc = getLatestAppointmentImageSrc(appointment)

  const clientName = appointment.clientDisplayName ?? "Someone"

  const proName = appointment.practice?.user?.name ?? "a professional"

  const timeDifference = formatTimeDifference(appointment.createdAt ?? new Date().toISOString())

  const proSlug = appointment.practice?.user?.slug
  const proUrl = proSlug ? `https://heal.me/${proSlug}` : "https://heal.me/"

  return {
    imgSrc,
    preSubHeadingText: `${clientName} booked with`,
    preSubHeadingShowListingStar: true,
    preSubHeadingClassName: "w-full min-w-0",
    mainHeadingText: proName,
    mainHeadingClassName: "w-full truncate",
    postSubHeadingText: timeDifference,
    destinationUrl: proUrl
  }
}

const isFetchingReducer = (
  state: Record<SocialProofNotificationItemType, boolean>,
  action: { type: SocialProofNotificationItemType; value: boolean }
) => {
  return {
    ...state,
    [action.type]: action.value
  }
}

export default function SocialProofComponent({ isMobile }: Readonly<{ isMobile: boolean }>) {
  // ----- States -----

  const [notificationItemsList, setNotificationItemsList] = useState<Array<SocialProofNotificationItem>>([])
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [isCurrentIndexVisible, setIsCurrentIndexVisible] = useState<boolean>(false)
  const [dismissAllNotifications, setDismissAllNotifications] = useState<boolean>(false)
  const [animatedAppointmentsCount, setAnimatedAppointmentsCount] = useState<number | null>(null)

  // ----- Reducers -----

  const [isFetchingState, dispatchIsFetching] = useReducer(isFetchingReducer, {
    appointmentsLast24hCount: false,
    latestAppointments: false
  })

  // ----- Memoized Values -----

  // Checks whether all fetching operations are complete (value == false)
  // If all values are false, this returns true
  const doneFetching = useMemo(() => Object.values(isFetchingState).every((value) => !value), [isFetchingState])
  const currentNotificationItem = useMemo(
    () => notificationItemsList?.[currentIndex] ?? null,
    [currentIndex, notificationItemsList]
  )
  const currentNotificationDisplayTime = useMemo(
    () => currentNotificationItem?.displayTime ?? DISPLAY_DURATION_MS,
    [currentNotificationItem]
  )
  const currentNotificationCardProps = useMemo(() => {
    if (!currentNotificationItem) return SOCIAL_PROOF_CARD_PROPS_EMPTY_VALUE

    if (
      currentNotificationItem.type !== "appointmentsLast24hCount" ||
      !currentNotificationItem.appointmentsCountTarget ||
      currentNotificationItem.appointmentsCountTarget <= APPOINTMENTS_COUNT_TICKER_START_VALUE
    ) {
      return currentNotificationItem.data
    }

    return {
      ...currentNotificationItem.data,
      mainHeadingText: formatAppointmentsLast24hCountHeading(
        animatedAppointmentsCount ?? APPOINTMENTS_COUNT_TICKER_START_VALUE
      )
    }
  }, [animatedAppointmentsCount, currentNotificationItem])

  // ----- Refs -----

  const displayNotificationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const appointmentsCountStartDelayRef = useRef<NodeJS.Timeout | null>(null)
  const appointmentsCountAnimationRef = useRef<NodeJS.Timeout | null>(null)
  const preloadedImageSrcsRef = useRef<Set<string>>(new Set())

  // ----- Callbacks -----

  const clearTimeouts = useCallback(() => {
    if (displayNotificationTimeoutRef.current) clearTimeout(displayNotificationTimeoutRef.current)
    if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)
  }, [])

  const clearAppointmentsCountAnimation = useCallback(() => {
    if (appointmentsCountStartDelayRef.current) clearTimeout(appointmentsCountStartDelayRef.current)
    if (appointmentsCountAnimationRef.current) clearInterval(appointmentsCountAnimationRef.current)
    appointmentsCountStartDelayRef.current = null
    appointmentsCountAnimationRef.current = null
  }, [])

  const startAppointmentsCountAnimation = useCallback((targetCount: number) => {
    const totalIncrements = Math.max(0, targetCount - APPOINTMENTS_COUNT_TICKER_START_VALUE)

    if (totalIncrements === 0) {
      setAnimatedAppointmentsCount(targetCount)
      return
    }

    const maxTicks = Math.max(
      1,
      Math.floor(APPOINTMENTS_COUNT_TICKER_DURATION_MS / APPOINTMENTS_COUNT_TICKER_MIN_TICK_MS)
    )
    const incrementPerTick = Math.max(1, Math.ceil(totalIncrements / maxTicks))
    const ticksNeeded = Math.ceil(totalIncrements / incrementPerTick)
    const tickMs = APPOINTMENTS_COUNT_TICKER_DURATION_MS / ticksNeeded

    if (appointmentsCountAnimationRef.current) clearInterval(appointmentsCountAnimationRef.current)

    setAnimatedAppointmentsCount(APPOINTMENTS_COUNT_TICKER_START_VALUE)
    appointmentsCountAnimationRef.current = setInterval(() => {
      setAnimatedAppointmentsCount((prevCount) => {
        const nextCount = (prevCount ?? APPOINTMENTS_COUNT_TICKER_START_VALUE) + incrementPerTick

        if (nextCount >= targetCount) {
          if (appointmentsCountAnimationRef.current) clearInterval(appointmentsCountAnimationRef.current)
          appointmentsCountAnimationRef.current = null
          return targetCount
        }

        return nextCount
      })
    }, tickMs)
  }, [])

  const dismissAllNotificationsCallback = useCallback(() => {
    setDismissAllNotifications(true)
  }, [])

  // ----- useEfects -----

  useEffect(() => {
    const fetchAppointmentsLast24hCount = async () => {
      dispatchIsFetching({ type: "appointmentsLast24hCount", value: true })
      try {
        const result = await urqlClient.query(APPOINTMENTS_LAST_24H_COUNT_QUERY).toPromise()
        const count = Number(result?.data?.appointmentsLast24hCount)
        const parsedCount = Math.floor(count)
        if (!Number.isFinite(parsedCount) || parsedCount <= APPOINTMENTS_LAST_HOURS_COUNT_DISPLAY_THRESHOLD) {
          return
        }
        setNotificationItemsList((prevNotificationItemsList) => [
          ...prevNotificationItemsList,
          {
            type: "appointmentsLast24hCount",
            data: prepareAppointmentsLast24hCountProps(parsedCount),
            displayTime: DISPLAY_DURATION_MS,
            appointmentsCountTarget: parsedCount
          }
        ])
      } catch (error) {
        console.error("Error fetching appointments last 24h count:", error)
      } finally {
        dispatchIsFetching({ type: "appointmentsLast24hCount", value: false })
      }
    }

    const fetchLatestAppointments = async () => {
      dispatchIsFetching({ type: "latestAppointments", value: true })
      try {
        const result = await urqlClient
          .query(LATEST_APPOINTMENTS_QUERY, {
            lastNHours: LATEST_APPOINTMENTS_LAST_N_HOURS,
            limit: LATEST_APPOINTMENTS_LIMIT
          })
          .toPromise()
        const appointments = result?.data?.latestAppointments
        if (!appointments || appointments.length === 0) return
        appointments.forEach((appointment: LatestAppointmentsQueryData) => {
          const latestAppointmentsProps = prepareLatestAppointmentsProps(appointment)
          setNotificationItemsList((prevNotificationItemsList) => [
            ...prevNotificationItemsList,
            {
              type: "latestAppointments",
              data: latestAppointmentsProps,
              displayTime: DISPLAY_DURATION_MS
            }
          ])
        })
      } catch (error) {
        console.error("Error fetching latest appointments:", error)
      } finally {
        dispatchIsFetching({ type: "latestAppointments", value: false })
      }
    }

    fetchAppointmentsLast24hCount()
    fetchLatestAppointments()
  }, [])

  useEffect(() => {
    // Display the first notification item when all fetching operations are complete (doneFetching == true)
    if (doneFetching && notificationItemsList.length > 0) setIsCurrentIndexVisible(true)
  }, [doneFetching, notificationItemsList.length])

  useEffect(() => {
    if (typeof Image === "undefined") return

    notificationItemsList.forEach(({ data }) => {
      if (!data.imgSrc || preloadedImageSrcsRef.current.has(data.imgSrc)) return

      const image = new Image()
      image.src = data.imgSrc
      preloadedImageSrcsRef.current.add(data.imgSrc)
    })
  }, [notificationItemsList])

  useEffect(() => {
    if (dismissAllNotifications) {
      clearTimeouts()
      clearAppointmentsCountAnimation()
      setNotificationItemsList([])
      setCurrentIndex(0)
      setIsCurrentIndexVisible(false)
      setAnimatedAppointmentsCount(null)
    }
  }, [clearAppointmentsCountAnimation, clearTimeouts, dismissAllNotifications])

  useEffect(() => {
    clearAppointmentsCountAnimation()
    setAnimatedAppointmentsCount(null)

    const appointmentsCountTarget = currentNotificationItem?.appointmentsCountTarget

    if (
      !isCurrentIndexVisible ||
      currentNotificationItem?.type !== "appointmentsLast24hCount" ||
      !appointmentsCountTarget ||
      appointmentsCountTarget <= APPOINTMENTS_COUNT_TICKER_START_VALUE
    ) {
      return
    }

    setAnimatedAppointmentsCount(APPOINTMENTS_COUNT_TICKER_START_VALUE)
    appointmentsCountStartDelayRef.current = setTimeout(() => {
      startAppointmentsCountAnimation(appointmentsCountTarget)
    }, APPOINTMENTS_COUNT_TICKER_ENTER_DELAY_MS)

    return () => {
      clearAppointmentsCountAnimation()
    }
  }, [clearAppointmentsCountAnimation, currentNotificationItem, isCurrentIndexVisible, startAppointmentsCountAnimation])

  useEffect(() => {
    if (!currentNotificationItem) return

    if (isCurrentIndexVisible) {
      displayNotificationTimeoutRef.current = setTimeout(() => {
        setIsCurrentIndexVisible(false)
      }, currentNotificationDisplayTime)
    }

    if (!isCurrentIndexVisible) {
      delayTimeoutRef.current = setTimeout(() => {
        setCurrentIndex((prevCurrentIndex) =>
          prevCurrentIndex < notificationItemsList.length - 1 ? prevCurrentIndex + 1 : -1
        )
        setIsCurrentIndexVisible(true)
      }, DELAY_DURATION_MS)
    }

    return () => {
      clearTimeouts()
    }
  }, [
    clearTimeouts,
    currentNotificationDisplayTime,
    currentNotificationItem,
    isCurrentIndexVisible,
    notificationItemsList.length
  ])

  useEffect(() => {
    // When currentIndex is set to -1, it means that all notifications have been displayed
    // We then call dismissAllNotificationsCallback to dismiss all notifications
    // This is done to prevent memory leaks (i.e: currentIndex grows indefinitely)
    if (currentIndex === -1) {
      setDismissAllNotifications(true)
    }
  }, [currentIndex])

  return (
    <>
      {!dismissAllNotifications && currentNotificationItem && (
        <SocialProofWrapper isMobile={isMobile} isVisible={isCurrentIndexVisible}>
          <SocialProofCard
            key={`${currentIndex}-${currentNotificationItem.type}-${currentNotificationCardProps.imgSrc}`}
            props={currentNotificationCardProps}
            onDismissCallback={dismissAllNotificationsCallback}
            isMobile={isMobile}
          />
        </SocialProofWrapper>
      )}
    </>
  )
}
